import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  TextField,
  Select,
  MenuItem,
  useTheme,
  Button,
  Stack,
  Avatar,
  Alert,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableFooter,
  Breadcrumbs,
  Divider,
  Chip,
  Fade,
  InputAdornment,
  alpha,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  Visibility as VisibilityIcon,
  ArrowUpward as ArrowUpwardIcon,
  Dashboard as DashboardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { useAuthToken } from "../TokenContext";
import { useLocation, Link } from 'react-router-dom';
import { useIntl } from 'react-intl';
import moment from 'moment';
import { styled } from '@mui/material/styles';
import axios from 'axios';
import {
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';


const TabButton = ({ active, icon: Icon, label, onClick }) => (
  <button
    onClick={onClick}
    className={`artemy-btn ${active ? 'artemy-btn-primary' : 'artemy-btn-ghost'}`}
    style={{ minWidth: 140, justifyContent: 'center' }}
  >
    <Icon sx={{ fontSize: 18, mr: 1 }} />
    {label}
  </button>
);
const FilterPaper = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '16px',
  alignItems: 'center',
  flexWrap: 'wrap',
  marginBottom: '24px',
}));
const MainContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: '1200px',
  margin: '0 auto',
  padding: '24px',

  [theme.breakpoints.down('md')]: {
    padding: '16px',
  },

  [theme.breakpoints.down('sm')]: {
    padding: '12px',
  },

  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
}));
const StyledTabs = styled(Box)(({ theme }) => ({
  display: 'flex',
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius * 2,
  padding: theme.spacing(1),
  marginBottom: theme.spacing(4),
  '& button': {
    flex: 1,
    padding: theme.spacing(1.5),
    border: 'none',
    borderRadius: theme.shape.borderRadius,
    fontFamily: 'Poppins, sans-serif',
    fontWeight: 600,
    fontSize: '1rem',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    '&.active': {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
    }
  }
}));

const StyledForm = styled('form')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
  width: '100%',
}));

const PodactivitytalentRetro = ({ collapsed }) => {
  const theme = useTheme();
  const intl = useIntl();
  const token = useAuthToken();
  const { user } = useSelector(state => state.auth);
  // const location = useLocation();
  // const { podid } = location.state;
  // Add this new state for selected pod
  const [selectedPod, setSelectedPod] = useState(null);
  const [selectedPodId, setSelectedPodId] = useState(null);
  // State
  const [activeTab, setActiveTab] = useState('post');
  const [sprintData, setSprintData] = useState([]);
  const [selectedSprint, setSelectedSprint] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [retroData, setRetroData] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [sprintFilter, setSprintFilter] = useState('');
  const [teamFilter, setTeamFilter] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  const [loading, setLoading] = useState(false);
  const [podDetails, setPodDetails] = useState([]);

  // Form state
  const [formData, setFormData] = useState({
    pod_feedback: '',
    start_doing: '',
    stop_doing: '',
    keep_doing: '',
    sprint: '',
  });

  // Fetch sprint data
  useEffect(() => {
    if (token) {
      fetchSprintData();
    }
  }, [token]);

  // Fetch retro data when pod is selected
  useEffect(() => {
    if (token && selectedPodId) {
      fetchRetroData();
    }
  }, [token, selectedPodId]);

  useEffect(() => {
    const fetchPodDetails = async () => {
      try {
        const response = await axios.post(
          process.env.REACT_APP_TALENT_PODLISTTALENT,
          { candidateID: user.data[0].c_id },
          { headers: { Authorization: token, 'Content-Type': 'application/json' } }
        );
        const pods = response?.data?.data;
        setPodDetails(pods);

        // Auto-select first pod if available
        if (pods && pods.length > 0) {
          setSelectedPod(pods[0]);
          setSelectedPodId(pods[0].podID); // Using podID from your API structure
        }

        setLoading(false);
      } catch (err) {
        console.error("Error:", err);
        setLoading(false);
      }
    };

    fetchPodDetails();
  }, [token, user]);

  const fetchSprintData = async () => {
    try {
      const response = await axios.post(
        process.env.REACT_APP_TALENT_SPRINTFILTERTAENT,
        { talent_id: user.data[0].c_id },
        {
          headers: {
            Authorization: token,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.statusCode === 200 && response.data.body?.sprint_filter) {
        setSprintData(response.data.body.sprint_filter);
      }
    } catch (error) {
      showSnackbar('Failed to fetch sprint data', 'error');
    }
  };

  const fetchRetroData = async () => {
    if (!selectedPodId) return;

    try {
      const response = await axios.post(
        process.env.REACT_APP_CLIENT_PODRETROLIST,
        { pods_id: selectedPodId },
        {
          headers: {
            Authorization: token,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.data) {
        const processedData = response.data.data.map(item => ({
          ...item,
          retroFields: parseRetroFields(item.retro)
        }));
        setRetroData(processedData);
      }
    } catch (error) {
      showSnackbar('Failed to fetch retro data', 'error');
    }
  };

  const parseRetroFields = (retroString) => {
    try {
      return JSON.parse(retroString) || {};
    } catch (error) {
      return {};
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const retroFeedback = {
      start_doing: formData.start_doing,
      stop_doing: formData.stop_doing,
      keep_doing: formData.keep_doing
    };

    const payload = {
      pod_feedback: formData.pod_feedback,
      retro: retroFeedback,
      sprint: selectedSprint,
      status: "Completed",
      pods_id: selectedPodId,
      submitted_by: user.data[0].firstName,
      submitted_by_id: user.data[0].c_id
    };

    try {
      const response = await axios.post(
        process.env.REACT_APP_TALENT_PODRETROS,
        payload,
        {
          headers: {
            Authorization: token,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.status === 200) {
        showSnackbar('Retro posted successfully', 'success');
        resetForm();
        fetchRetroData();
      } else {
        throw new Error(response.data.data || 'Failed to post retro');
      }
    } catch (error) {
      showSnackbar(error.message || 'Failed to post retro', 'error');
    }
  };

  // Helper Functions
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const resetForm = () => {
    setFormData({
      pod_feedback: '',
      start_doing: '',
      stop_doing: '',
      keep_doing: '',
      sprint: '',
    });
    setSelectedSprint('');
  };

  // Filtered and sorted data
  const filteredAndSortedData = useMemo(() => {
    let result = retroData;

    if (teamFilter) {
      result = result.filter(item => item.submitted_by === teamFilter);
    }

    return result.sort((a, b) => {
      const dateA = moment(a.createdAt);
      const dateB = moment(b.createdAt);
      return sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
    });
  }, [retroData, teamFilter, sortDirection]);

  // Render Functions
  const renderPostForm = () => (
    <div className="artemy-card" style={{ padding: '32px' }}>
      <Typography variant="h6" gutterBottom color="primary" sx={{ fontFamily: 'var(--artemy-font-display)', fontWeight: 600, color: 'var(--artemy-text-primary) !important', mb: 3 }}>
        {intl.formatMessage({ id: 'newRetro' })}
      </Typography>

      <StyledForm onSubmit={handleSubmit}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <label style={{ fontSize: '13px', color: 'var(--artemy-text-secondary)', fontWeight: 500 }}>
            {intl.formatMessage({ id: 'podRetroFeedback' })} *
          </label>
          <textarea
            required
            rows={4}
            value={formData.pod_feedback}
            onChange={(e) => setFormData(prev => ({ ...prev, pod_feedback: e.target.value }))}
            style={{
              background: 'var(--artemy-bg-base)',
              border: '1px solid var(--artemy-border)',
              borderRadius: '8px',
              padding: '12px',
              color: 'var(--artemy-text-primary)',
              fontFamily: 'inherit',
              fontSize: '14px',
              resize: 'vertical'
            }}
          />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <label style={{ fontSize: '13px', color: 'var(--artemy-text-secondary)', fontWeight: 500 }}>
            {intl.formatMessage({ id: 'selectSprint' })} *
          </label>
          <select
            required
            value={selectedSprint}
            onChange={(e) => setSelectedSprint(e.target.value)}
            style={{
              background: 'var(--artemy-bg-base)',
              border: '1px solid var(--artemy-border)',
              borderRadius: '8px',
              padding: '12px',
              color: 'var(--artemy-text-primary)',
              fontFamily: 'inherit',
              fontSize: '14px'
            }}
          >
            <option value="">{intl.formatMessage({ id: 'selectSprint' })}</option>
            {sprintData.map((sprint, index) => (
              <option key={index} value={sprint.sprint_name}>
                {sprint.sprint_name}
              </option>
            ))}
          </select>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <label style={{ fontSize: '13px', color: 'var(--artemy-text-secondary)', fontWeight: 500 }}>
            {intl.formatMessage({ id: 'startDoing' })} *
          </label>
          <textarea
            required
            rows={3}
            value={formData.start_doing}
            onChange={(e) => setFormData(prev => ({ ...prev, start_doing: e.target.value }))}
            style={{
              background: 'var(--artemy-bg-base)',
              border: '1px solid var(--artemy-border)',
              borderRadius: '8px',
              padding: '12px',
              color: 'var(--artemy-text-primary)',
              fontFamily: 'inherit',
              fontSize: '14px',
              resize: 'vertical'
            }}
          />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <label style={{ fontSize: '13px', color: 'var(--artemy-text-secondary)', fontWeight: 500 }}>
            {intl.formatMessage({ id: 'stopDoing' })} *
          </label>
          <textarea
            required
            rows={3}
            value={formData.stop_doing}
            onChange={(e) => setFormData(prev => ({ ...prev, stop_doing: e.target.value }))}
            style={{
              background: 'var(--artemy-bg-base)',
              border: '1px solid var(--artemy-border)',
              borderRadius: '8px',
              padding: '12px',
              color: 'var(--artemy-text-primary)',
              fontFamily: 'inherit',
              fontSize: '14px',
              resize: 'vertical'
            }}
          />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <label style={{ fontSize: '13px', color: 'var(--artemy-text-secondary)', fontWeight: 500 }}>
            {intl.formatMessage({ id: 'keepDoing' })} *
          </label>
          <textarea
            required
            rows={3}
            value={formData.keep_doing}
            onChange={(e) => setFormData(prev => ({ ...prev, keep_doing: e.target.value }))}
            style={{
              background: 'var(--artemy-bg-base)',
              border: '1px solid var(--artemy-border)',
              borderRadius: '8px',
              padding: '12px',
              color: 'var(--artemy-text-primary)',
              fontFamily: 'inherit',
              fontSize: '14px',
              resize: 'vertical'
            }}
          />
        </div>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <button type="submit" className="artemy-btn artemy-btn-primary" style={{ padding: '10px 24px' }}>
            {intl.formatMessage({ id: 'postRetro' })}
          </button>
        </Box>
      </StyledForm>
    </div>
  );
  const renderPodSelector = () => (
    <div style={{ marginBottom: '24px', display: 'flex', gap: '16px', alignItems: 'center' }}>
      <div style={{ flex: 1, maxWidth: 300 }}>
        <select
          value={selectedPodId || ''}
          onChange={(e) => {
            const selectedId = e.target.value;
            const pod = podDetails.find(p => p.podID === selectedId);
            setSelectedPod(pod);
            setSelectedPodId(selectedId);
          }}
          style={{
            background: 'var(--artemy-bg-base)',
            border: '1px solid var(--artemy-border)',
            borderRadius: '8px',
            padding: '12px 16px',
            color: 'var(--artemy-text-primary)',
            fontFamily: 'var(--artemy-font-mono)',
            fontSize: '13px',
            width: '100%',
            cursor: 'pointer'
          }}
        >
          <option value="" disabled>{intl.formatMessage({ id: 'selectPod', defaultMessage: 'Select Pod' })}</option>
          {podDetails.map((pod) => (
            <option key={pod.podID} value={pod.podID}>
              {pod.podName} ({pod.status})
            </option>
          ))}
        </select>
      </div>

      {selectedPod && (
        <div style={{
          padding: '6px 12px',
          borderRadius: '100px',
          border: '1px solid var(--artemy-accent)',
          background: 'rgba(var(--artemy-primary-rgb), 0.1)',
          color: 'var(--artemy-primary)',
          fontSize: '12px',
          fontFamily: 'var(--artemy-font-mono)'
        }}>
          Selected: {selectedPod.podName}
        </div>
      )}
    </div>
  );
  const renderTabs = () => (
    <Stack
      direction="row"
      spacing={2}
      sx={{
        mb: 4,
        p: 1,
        borderRadius: '20px',
        backgroundColor: alpha(theme.palette.background.paper, 0.6),
        backdropFilter: 'blur(8px)',
      }}
    >
      <TabButton
        active={activeTab === 'post'}
        icon={AssignmentIcon}
        label={intl.formatMessage({ id: 'postRetro' })}
        onClick={() => setActiveTab('post')}
      />
      <TabButton
        active={activeTab === 'view'}
        icon={VisibilityIcon}
        label={intl.formatMessage({ id: 'viewRetros' })}
        onClick={() => setActiveTab('view')}
      />
    </Stack>
  );


  const renderFilters = () => (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      spacing={2}
      sx={{
        mb: 3,
        p: 2,
        borderRadius: '16px',
        backgroundColor: alpha(theme.palette.background.paper, 0.8),
        backdropFilter: 'blur(8px)',
      }}
    >
      <Select
        value={teamFilter}
        onChange={(e) => setTeamFilter(e.target.value)}
        displayEmpty
        startAdornment={<PersonIcon sx={{ mr: 1, color: 'primary.main' }} />}
        sx={{
          minWidth: 200,
          '& .MuiSelect-select': {
            py: 1.5,
          },
        }}
      >
        <MenuItem value="">{intl.formatMessage({ id: 'teamMember' })}</MenuItem>
        {Array.from(new Set(retroData.map(item => item.submitted_by)))
          .map((member, index) => (
            <MenuItem key={index} value={member}>
              <Stack direction="row" spacing={1} alignItems="center">
                <PersonIcon fontSize="small" />
                {member}
              </Stack>
            </MenuItem>
          ))}
      </Select>

      <Select
        value={sprintFilter}
        onChange={(e) => setSprintFilter(e.target.value)}
        displayEmpty
        startAdornment={<RefreshIcon sx={{ mr: 1, color: 'primary.main' }} />}
        sx={{ minWidth: 200 }}
      >
        <MenuItem value="">{intl.formatMessage({ id: 'selectSprint' })}</MenuItem>
        {Array.from(new Set(retroData.map(item => item.sprint)))
          .map((sprint, index) => (
            <MenuItem key={index} value={sprint}>{sprint}</MenuItem>
          ))}
      </Select>

      <Button
        startIcon={sortDirection === 'asc' ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
        onClick={() => setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')}
        variant="outlined"
        sx={{ minWidth: 140 }}
      >
        {intl.formatMessage({ id: 'sortByDate' })}
      </Button>
    </Stack>
  );

  // First, calculate the filtered data
  const filteredData = useMemo(() => {
    return retroData
      .filter(item => !teamFilter || item.submitted_by === teamFilter)
      .filter(item => !sprintFilter || item.sprint === sprintFilter)
      .sort((a, b) => {
        const dateA = moment(a.createdAt);
        const dateB = moment(b.createdAt);
        return sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
      });
  }, [retroData, teamFilter, sprintFilter, sortDirection]);
  useEffect(() => {
    setPage(0);
  }, [teamFilter, sprintFilter]); // Reset to first page when filters change
  const renderRetroTable = () => (
    <Fade in>
      <div className="artemy-card" style={{ overflow: 'hidden' }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--artemy-border)' }}>
          {renderFilters()}
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr>
              {[
                intl.formatMessage({ id: 'teamMember' }),
                intl.formatMessage({ id: 'date' }),
                intl.formatMessage({ id: 'sprint' }),
                intl.formatMessage({ id: 'startDoing' }),
                intl.formatMessage({ id: 'stopDoing' }),
                intl.formatMessage({ id: 'keepDoing' }),
                intl.formatMessage({ id: 'status' })
              ].map((h) => (
                <th key={h} style={{ textAlign: 'left', padding: '11px 16px', fontFamily: 'var(--artemy-font-mono)', fontSize: 10, letterSpacing: '.8px', color: 'var(--artemy-text-dim)', textTransform: 'uppercase', borderBottom: '1px solid var(--artemy-border)', fontWeight: 400 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredData
              .filter(item => !teamFilter || item.submitted_by === teamFilter)
              .filter(item => !sprintFilter || item.sprint === sprintFilter)
              .sort((a, b) => {
                const dateA = moment(a.createdAt);
                const dateB = moment(b.createdAt);
                return sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
              })
              .slice(page * rowsPerPage, (page + 1) * rowsPerPage)
              .map((item, index) => (
                <tr key={item.id} style={{ borderBottom: '1px solid rgba(255,255,255,.03)', transition: 'background .15s' }}>
                  <td style={{ padding: '12px 16px', fontWeight: 600, color: 'var(--artemy-text-primary)' }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem', bgcolor: 'var(--artemy-primary)' }}>
                        {item.submitted_by?.[0] || 'U'}
                      </Avatar>
                      <span>{item.submitted_by}</span>
                    </Stack>
                  </td>
                  <td style={{ padding: '12px 16px', color: 'var(--artemy-text-secondary)', fontFamily: 'var(--artemy-font-mono)', fontSize: 11 }}>
                    {moment(item.createdAt).format('MMM DD, YYYY')}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{ fontFamily: 'var(--artemy-font-mono)', fontSize: 10, padding: '2px 8px', borderRadius: 4, display: 'inline-block', color: 'var(--artemy-primary)', background: 'rgba(var(--artemy-primary-rgb), 0.1)', border: '1px solid rgba(var(--artemy-primary-rgb), 0.2)' }}>
                      {item.sprint}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{ fontSize: 12, padding: '4px 8px', borderRadius: 4, display: 'inline-block', color: 'var(--artemy-green)', background: 'rgba(0,212,170,.1)' }}>
                      {item.retroFields.start_doing}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{ fontSize: 12, padding: '4px 8px', borderRadius: 4, display: 'inline-block', color: 'var(--artemy-red)', background: 'rgba(255,77,106,.1)' }}>
                      {item.retroFields.stop_doing}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{ fontSize: 12, padding: '4px 8px', borderRadius: 4, display: 'inline-block', color: 'var(--artemy-blue)', background: 'rgba(61,127,255,.1)' }}>
                      {item.retroFields.keep_doing}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{ fontFamily: 'var(--artemy-font-mono)', fontSize: 10, padding: '2px 8px', borderRadius: 4, display: 'inline-block', color: item.status === 'Completed' ? 'var(--artemy-green)' : 'var(--artemy-red)', background: item.status === 'Completed' ? 'rgba(0,212,170,.1)' : 'rgba(255,77,106,.1)', border: `1px solid ${item.status === 'Completed' ? 'var(--artemy-green)' : 'var(--artemy-red)'}33` }}>
                      {item.status}
                    </span>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
        <div style={{ padding: '16px', display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid var(--artemy-border)' }}>
          <TablePagination
            component="div"
            count={filteredData.length}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[5]}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            sx={{
              color: 'var(--artemy-text-secondary)',
              '.MuiTablePagination-selectIcon': { color: 'var(--artemy-text-secondary)' },
              '.MuiIconButton-root': { color: 'var(--artemy-text-primary)' }
            }}
          />
        </div>
      </div>
    </Fade>
  );


  return (
    <MainContainer collapsed={collapsed}>
      {/* Page Header */}
      <div style={{ marginBottom: '28px', paddingBottom: '20px', borderBottom: '1px solid var(--artemy-border)' }}>
        <h1 style={{
          fontFamily: 'var(--artemy-font-display)',
          fontSize: '24px',
          fontWeight: 700,
          color: 'var(--artemy-text-primary)',
          margin: 0,
          letterSpacing: '-0.4px'
        }}>
          POD Retrospective
        </h1>
        <p style={{
          fontSize: '13px',
          color: 'var(--artemy-text-secondary)',
          margin: '6px 0 0',
          fontFamily: 'var(--artemy-font-body)',
        }}>
          Review and submit your sprint retros
        </p>
      </div>

      <Stack spacing={3}>
        {/* <Stack 
    direction="row" 
    alignItems="center" 
    spacing={2}
    sx={{ mb: 2 }}
  >
    <Tooltip title="Back to My Pods">
      <IconButton
        component={Link}
        to="/Pods"
        sx={{ 
          backgroundColor: alpha(theme.palette.primary.main, 0.1),
          '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.2),
          }
        }}
      >
        <ArrowBackIcon />
      </IconButton>
    </Tooltip>
    <Breadcrumbs>
      <Link
        to="/Pods"
        style={{
          color: theme.palette.primary.main,
          textDecoration: 'none',
          fontWeight: 600,
        }}
      >
       {intl.formatMessage({ id: 'myPods' })}
      </Link>
      <Typography color="textSecondary" fontWeight={600}>
        {activeTab === 'post' ? intl.formatMessage({ id: 'postRetro' }) :   intl.formatMessage({ id: 'viewRetros' })}
      </Typography>
    </Breadcrumbs>
  </Stack> */}

        {renderTabs()}
        {renderPodSelector()}
        {activeTab === 'view' ? renderRetroTable() : renderPostForm()}
      </Stack>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert
          elevation={6}
          variant="filled"
          severity={snackbar.severity}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </MainContainer>
  );
}

export default PodactivitytalentRetro;